<script setup lang="ts">
import QRCode from 'qrcode'
import { AFFILIATE_LINK_TARGETS, AFFILIATE_PAGE_PREFIX, AFFILIATE_SHARE_NETWORKS, type AffiliateLinkTargetId, type AffiliateShareNetwork, shareUrl, withAffiliateRef } from '~/constants/affiliate'
import { APP_LOCALES, type AppLocale, isAppLocale } from '~/constants/appLocale'

/**
 * The partner's link, ready to share without editing anything: pick the page
 * (their own page first, any page of the site, or a pasted address) and the
 * language, then copy it, share it to a network in one click or download it
 * as a QR code. Each way out carries its own ?src= tag, so the statistics
 * show which one brings visitors.
 */
const props = defineProps<{
  code: string
  /** Their discount code, if they made one: the share text gives it out. */
  discountCode: string | null
  discountPercent: number | null
}>()

const { t, locale } = useI18n()
const { show: showSnackbar } = useSnackbar()
const { siteUrl, absUrl } = useSiteUrl()
const localize = useLocalizedPath()

const target = ref<AffiliateLinkTargetId | 'custom'>('partnerPage')
const lang = ref<AppLocale>(isAppLocale(locale.value) ? locale.value : 'en')
const customUrl = ref('')

const LANG_NAMES: Record<AppLocale, string> = { en: 'English', cs: 'Čeština', de: 'Deutsch', pl: 'Polski' }

/** The link for the chosen page, tagged with `source`; null for an address off our site. */
function linkFor(source = ''): string | null {
  if (target.value === 'custom') {
    return customUrl.value.trim() ? withAffiliateRef(customUrl.value, props.code, siteUrl, source) : null
  }
  const path = AFFILIATE_LINK_TARGETS.find(item => item.id === target.value)?.path ?? null
  if (path === null) {
    // Their own page records the click itself; ?ref would only lengthen it.
    const page = absUrl(localize(`${AFFILIATE_PAGE_PREFIX}${props.code}`, lang.value)) ?? ''
    return source ? `${page}?src=${source}` : page
  }
  return withAffiliateRef(absUrl(localize(path, lang.value)) ?? '', props.code, siteUrl, source)
}

const link = computed(() => linkFor())
const customError = computed(() => target.value === 'custom' && customUrl.value.trim() && !link.value
  ? t('affiliate.builder.customError')
  : '')
/** "inmyresume.com/p/jana": what to say in a video or print on a flyer. */
const spoken = computed(() => target.value === 'partnerPage' && link.value
  ? link.value.replace(/^https?:\/\/(www\.)?/, '')
  : '')

const shareText = computed(() => props.discountCode
  ? t('affiliate.share.textCode', { percent: props.discountPercent ?? '', code: props.discountCode })
  : t('affiliate.share.text'))

async function copy(text: string, message = t('affiliate.link.copied')) {
  try {
    await navigator.clipboard.writeText(text)
    showSnackbar(message)
    return true
  } catch {
    // Clipboard blocked: the field is selectable by hand.
    return false
  }
}

async function share(network: AffiliateShareNetwork) {
  const url = linkFor(`share-${network}`)
  if (!url) return
  // LinkedIn and Facebook take the link only; the text goes to the clipboard.
  if (network === 'linkedin' || network === 'facebook') {
    await copy(`${shareText.value} ${url}`, t('affiliate.share.textCopied'))
  }
  const href = shareUrl(network, url, shareText.value, t('affiliate.share.subject'))
  if (network === 'email') window.location.href = href
  else window.open(href, '_blank', 'noopener,noreferrer,width=680,height=640')
}

/** The phone's own share sheet, where there is one (mostly phones). */
const canShareNatively = ref(false)
onMounted(() => {
  canShareNatively.value = typeof navigator.share === 'function'
})
async function shareNatively() {
  const url = linkFor('share-native')
  if (!url) return
  await navigator.share({ title: 'InMyResume', text: shareText.value, url }).catch(() => {})
}

const NETWORK_ICONS: Record<AffiliateShareNetwork, string> = {
  linkedin: 'bi-linkedin',
  facebook: 'bi-facebook',
  whatsapp: 'bi-whatsapp',
  x: 'bi-twitter-x',
  telegram: 'bi-telegram',
  email: 'bi-envelope',
}

// ---- QR code: a preview, and a large PNG to print ----
const qrPreview = ref('')
const qrLink = computed(() => linkFor('qr'))
watch(qrLink, async (value) => {
  qrPreview.value = value ? await QRCode.toDataURL(value, { width: 240, margin: 1 }).catch(() => '') : ''
}, { immediate: true })

async function downloadQr() {
  if (!qrLink.value) return
  const dataUrl = await QRCode.toDataURL(qrLink.value, { width: 1200, margin: 2 }).catch(() => '')
  if (!dataUrl) return
  const a = document.createElement('a')
  a.href = dataUrl
  a.download = `inmyresume-${props.code}-qr.png`
  a.click()
}
</script>

<template>
  <div class="rounded-[0.625rem] border border-line p-6 dark:border-gray-700">
    <h2 class="!text-xl">{{ t('affiliate.link.title') }}</h2>

    <div class="mt-4 grid gap-4 sm:grid-cols-[1fr_auto]">
      <SofaxSelect v-model="target" :label="t('affiliate.builder.target')">
        <option v-for="item in AFFILIATE_LINK_TARGETS" :key="item.id" :value="item.id">
          {{ t(`affiliate.builder.targets.${item.id}`) }}
        </option>
        <option value="custom">{{ t('affiliate.builder.targets.custom') }}</option>
      </SofaxSelect>
      <SofaxSelect v-if="target !== 'custom'" v-model="lang" :label="t('affiliate.builder.language')">
        <option v-for="code in APP_LOCALES" :key="code" :value="code">{{ LANG_NAMES[code] }}</option>
      </SofaxSelect>
    </div>

    <SofaxInput
      v-if="target === 'custom'"
      v-model="customUrl"
      class="mt-4"
      :label="t('affiliate.builder.customLabel')"
      :placeholder="`${siteUrl}/blog/…`"
      :error="customError"
    />

    <div class="mt-5 flex flex-col gap-5 sm:flex-row sm:items-start">
      <div class="min-w-0 flex-1">
        <div class="flex flex-col gap-3 sm:flex-row">
          <input
            :value="link ?? ''"
            readonly
            class="field-input flex-1"
            :aria-label="t('affiliate.link.title')"
            @focus="($event.target as HTMLInputElement).select()"
          >
          <SofaxBtn :label="t('affiliate.link.copy')" variant="dark" :disabled="!link" @click="link && copy(link)" />
        </div>
        <p v-if="spoken" class="mt-3 text-sm text-muted dark:text-gray-400">
          <i class="bi bi-megaphone" aria-hidden="true" />
          {{ t('affiliate.builder.spoken', { link: spoken }) }}
        </p>
        <p v-else class="mt-3 text-sm text-muted dark:text-gray-400">{{ t('affiliate.link.hint', { code }) }}</p>

        <div class="mt-5">
          <div class="text-sm font-semibold text-ink dark:text-white">{{ t('affiliate.share.title') }}</div>
          <div class="mt-2 flex flex-wrap gap-2">
            <button
              v-for="network in AFFILIATE_SHARE_NETWORKS"
              :key="network"
              type="button"
              class="btn btn-outline btn-sm"
              :disabled="!link"
              @click="share(network)"
            >
              <i :class="['bi', NETWORK_ICONS[network]]" aria-hidden="true" />
              {{ t(`affiliate.share.networks.${network}`) }}
            </button>
            <button v-if="canShareNatively" type="button" class="btn btn-outline btn-sm" :disabled="!link" @click="shareNatively">
              <i class="bi bi-share" aria-hidden="true" />
              {{ t('affiliate.share.more') }}
            </button>
          </div>
          <p class="mt-2 text-xs text-muted dark:text-gray-400">{{ t('affiliate.share.hint') }}</p>
        </div>
      </div>

      <div v-if="qrPreview" class="flex shrink-0 flex-row items-center gap-3 sm:flex-col">
        <img :src="qrPreview" :alt="t('affiliate.builder.qrAlt')" width="120" height="120" class="size-[120px] rounded-lg border border-line bg-white dark:border-gray-700">
        <button type="button" class="btn btn-outline btn-sm" @click="downloadQr">
          <i class="bi bi-qr-code" aria-hidden="true" />
          {{ t('affiliate.builder.qrDownload') }}
        </button>
      </div>
    </div>
  </div>
</template>
